"use client";

import { c as _c } from "react/compiler-runtime";
import { ClipboardList, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import ReferralCardEditable from "@/src/components/cards/referral-card/referral-card-editable";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { referralStatusIcons, defaultReferralStatusIconAndColor, schedulingPriorityIcons } from "@/src/components/tasks/model-icons";
import { useUser } from "@/src/context/user-context";
import { usePriorAuthorizationsByReferralId } from "@/src/lib/data-fetchers/prior-authorization-fetchers";
import { canWriteSOR } from "@/src/lib/rbac";
import { formatFaxParens, formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral, ReferralStatus, SchedulingPriority, referralStatusLabel } from "@/src/models/documents/referral";
import { getPatientName } from "@/src/models/patient";
export default function ReferralCard(t0) {
  const $ = _c(156);
  const {
    referral,
    isEditable: t1
  } = t0;
  const isEditable = t1 === undefined ? false : t1;
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const {
    roles
  } = useUser();
  let t2;
  if ($[0] !== roles) {
    t2 = canWriteSOR(roles);
    $[0] = roles;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canWriteSor = t2;
  const effectiveIsEditable = isEditable;
  const {
    priorAuthorizations
  } = usePriorAuthorizationsByReferralId(referral?.id ?? null);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let cardActionsProps;
  let diagnosisList;
  let hasDiagnoses;
  let referringFax;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== canWriteSor || $[3] !== effectiveIsEditable || $[4] !== isEditing || $[5] !== pathname || $[6] !== priorAuthorizations || $[7] !== referral || $[8] !== router) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const authExpirationDate = priorAuthorizations.find(_temp)?.endDate ?? null;
      if (!referral) {
        t16 = null;
        break bb0;
      }
      let t17;
      if ($[32] !== isEditing) {
        t17 = () => {
          setIsEditing(!isEditing);
        };
        $[32] = isEditing;
        $[33] = t17;
      } else {
        t17 = $[33];
      }
      const handleEditToggle = t17;
      if (isEditing) {
        let t18;
        if ($[34] !== handleEditToggle || $[35] !== referral) {
          t18 = <ReferralCardEditable referral={referral} onClose={handleEditToggle} />;
          $[34] = handleEditToggle;
          $[35] = referral;
          $[36] = t18;
        } else {
          t18 = $[36];
        }
        t16 = t18;
        break bb0;
      }
      const patientDisplayId = referral.patient?.displayId;
      let t18;
      if ($[37] !== referral.patient) {
        t18 = referral.patient ? getPatientName(referral.patient) : null;
        $[37] = referral.patient;
        $[38] = t18;
      } else {
        t18 = $[38];
      }
      const patientName = t18;
      let t19;
      if ($[39] !== canWriteSor || $[40] !== handleEditToggle || $[41] !== patientDisplayId || $[42] !== referral.displayId || $[43] !== referral.id || $[44] !== router) {
        const customActions = [...(canWriteSor ? [{
          label: "Edit Referral",
          onClick: handleEditToggle
        }] : []), ...(patientDisplayId ? [{
          label: "View Patient",
          onClick: () => router.push(`/patients/${patientDisplayId}`)
        }] : [])];
        t19 = createCardActionsProps({
          id: referral.id,
          label: "Referral ID",
          href: `/referrals/${referral.displayId}`,
          customProps: customActions
        });
        $[39] = canWriteSor;
        $[40] = handleEditToggle;
        $[41] = patientDisplayId;
        $[42] = referral.displayId;
        $[43] = referral.id;
        $[44] = router;
        $[45] = t19;
      } else {
        t19 = $[45];
      }
      cardActionsProps = t19;
      let t20;
      if ($[46] !== referral.diagnosisList) {
        t20 = Array.isArray(referral.diagnosisList) ? referral.diagnosisList : [];
        $[46] = referral.diagnosisList;
        $[47] = t20;
      } else {
        t20 = $[47];
      }
      diagnosisList = t20;
      hasDiagnoses = diagnosisList.length > 0 || Boolean(referral.diagnosisCode || referral.diagnosisText);
      const status = referral.status as ReferralStatus | null;
      const statusMeta = referralStatusIcons[status || ReferralStatus.NOT_STARTED] || defaultReferralStatusIconAndColor;
      const StatusIcon = statusMeta?.icon;
      const schedulingPriority = referral.schedulingPriority as SchedulingPriority | null;
      const schedulingPriorityMeta = schedulingPriority ? schedulingPriorityIcons[schedulingPriority] : null;
      const SchedulingPriorityIcon = schedulingPriorityMeta?.icon;
      let t21;
      if ($[48] !== schedulingPriority) {
        t21 = isStatOrUrgentPriority(schedulingPriority);
        $[48] = schedulingPriority;
        $[49] = t21;
      } else {
        t21 = $[49];
      }
      const showSchedulingPriorityFlag = t21;
      const t22 = referral.referringLocation?.faxPhoneNumber;
      let t23;
      if ($[50] !== t22) {
        t23 = formatFaxParens(t22);
        $[50] = t22;
        $[51] = t23;
      } else {
        t23 = $[51];
      }
      referringFax = t23;
      T4 = ContextMenu;
      T3 = ContextMenuTrigger;
      t15 = true;
      T2 = Card;
      t13 = "w-full";
      let t24;
      let t25;
      if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = <ClipboardList className="h-6 w-6 mr-2" />;
        t25 = <CardTitle className="text-md font-semibold">Referral Information</CardTitle>;
        $[52] = t24;
        $[53] = t25;
      } else {
        t24 = $[52];
        t25 = $[53];
      }
      let t26;
      if ($[54] !== SchedulingPriorityIcon || $[55] !== schedulingPriorityMeta?.color || $[56] !== showSchedulingPriorityFlag) {
        t26 = showSchedulingPriorityFlag && SchedulingPriorityIcon && <SchedulingPriorityIcon weight="fill" className={`ml-3 h-4 w-4 ${schedulingPriorityMeta?.color ?? ""}`} />;
        $[54] = SchedulingPriorityIcon;
        $[55] = schedulingPriorityMeta?.color;
        $[56] = showSchedulingPriorityFlag;
        $[57] = t26;
      } else {
        t26 = $[57];
      }
      let t27;
      if ($[58] !== pathname || $[59] !== referral.displayId) {
        t27 = !pathname?.startsWith(`/referrals/${referral.displayId}`) ? <Link className="text-muted-foreground hover:text-foreground" href={`/referrals/${referral.displayId}`} target="_blank" rel="noopener noreferrer"><SquareArrowOutUpRight className="size-4" /></Link> : null;
        $[58] = pathname;
        $[59] = referral.displayId;
        $[60] = t27;
      } else {
        t27 = $[60];
      }
      let t28;
      if ($[61] !== cardActionsProps || $[62] !== effectiveIsEditable) {
        t28 = effectiveIsEditable && <DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"><MoreHorizontal className="h-4 w-4" /><span className="sr-only">Open menu</span></Button></DropdownMenuTrigger><CardActions {...cardActionsProps} isContextMenu={false} /></DropdownMenu>;
        $[61] = cardActionsProps;
        $[62] = effectiveIsEditable;
        $[63] = t28;
      } else {
        t28 = $[63];
      }
      let t29;
      if ($[64] !== t27 || $[65] !== t28) {
        t29 = <div className="ml-auto flex items-center gap-2">{t27}{t28}</div>;
        $[64] = t27;
        $[65] = t28;
        $[66] = t29;
      } else {
        t29 = $[66];
      }
      if ($[67] !== t26 || $[68] !== t29) {
        t14 = <CardHeader><div className="flex items-center">{t24}{t25}{t26}{t29}</div></CardHeader>;
        $[67] = t26;
        $[68] = t29;
        $[69] = t14;
      } else {
        t14 = $[69];
      }
      T1 = CardContent;
      t6 = "grid grid-cols-3 gap-4 text-sm";
      if ($[70] !== patientName) {
        t7 = patientName && <div className="space-y-1"><span className="font-semibold block">Patient</span><CopyableContent text={patientName} /></div>;
        $[70] = patientName;
        $[71] = t7;
      } else {
        t7 = $[71];
      }
      let t30;
      if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = <span className="font-semibold block">Status</span>;
        $[72] = t30;
      } else {
        t30 = $[72];
      }
      let t31;
      if ($[73] !== StatusIcon || $[74] !== statusMeta?.color) {
        t31 = StatusIcon && <StatusIcon weight="fill" className={`h-4 w-4 ${statusMeta?.color ?? ""}`} />;
        $[73] = StatusIcon;
        $[74] = statusMeta?.color;
        $[75] = t31;
      } else {
        t31 = $[75];
      }
      const t32 = status || ReferralStatus.NOT_STARTED;
      let t33;
      if ($[76] !== t32) {
        t33 = referralStatusLabel(t32);
        $[76] = t32;
        $[77] = t33;
      } else {
        t33 = $[77];
      }
      let t34;
      if ($[78] !== t33) {
        t34 = <span className="no-select">{t33}</span>;
        $[78] = t33;
        $[79] = t34;
      } else {
        t34 = $[79];
      }
      if ($[80] !== t31 || $[81] !== t34) {
        t8 = <div className="space-y-1">{t30}<div className="flex items-center gap-2">{t31}{t34}</div></div>;
        $[80] = t31;
        $[81] = t34;
        $[82] = t8;
      } else {
        t8 = $[82];
      }
      let t35;
      if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
        t35 = <span className="font-semibold block">Date of Referral</span>;
        $[83] = t35;
      } else {
        t35 = $[83];
      }
      let t36;
      if ($[84] !== referral.dateOfReferral) {
        t36 = referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : "N/A";
        $[84] = referral.dateOfReferral;
        $[85] = t36;
      } else {
        t36 = $[85];
      }
      if ($[86] !== t36) {
        t9 = <div className="space-y-1">{t35}<CopyableContent text={t36} /></div>;
        $[86] = t36;
        $[87] = t9;
      } else {
        t9 = $[87];
      }
      let t37;
      if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
        t37 = <span className="font-semibold block">Date Received</span>;
        $[88] = t37;
      } else {
        t37 = $[88];
      }
      let t38;
      if ($[89] !== referral.dateReceived) {
        t38 = referral.dateReceived ? formatIsoDate(referral.dateReceived) : "N/A";
        $[89] = referral.dateReceived;
        $[90] = t38;
      } else {
        t38 = $[90];
      }
      if ($[91] !== t38) {
        t10 = <div className="space-y-1">{t37}<CopyableContent text={t38} /></div>;
        $[91] = t38;
        $[92] = t10;
      } else {
        t10 = $[92];
      }
      let t39;
      if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
        t39 = <span className="font-semibold block">Service Line</span>;
        $[93] = t39;
      } else {
        t39 = $[93];
      }
      const t40 = referral.serviceLine || "N/A";
      if ($[94] !== t40) {
        t11 = <div className="space-y-1">{t39}<CopyableContent text={t40} /></div>;
        $[94] = t40;
        $[95] = t11;
      } else {
        t11 = $[95];
      }
      let t41;
      if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
        t41 = <span className="font-semibold block">Authorization Number</span>;
        $[96] = t41;
      } else {
        t41 = $[96];
      }
      const t42 = referral.authorizationNumber || "N/A";
      if ($[97] !== t42) {
        t12 = <div className="space-y-1">{t41}<CopyableContent text={t42} /></div>;
        $[97] = t42;
        $[98] = t12;
      } else {
        t12 = $[98];
      }
      t4 = "space-y-1";
      if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <span className="font-semibold block">Authorization Expiration Date</span>;
        $[99] = t5;
      } else {
        t5 = $[99];
      }
      T0 = CopyableContent;
      t3 = authExpirationDate ? formatIsoDate(authExpirationDate) : "N/A";
    }
    $[2] = canWriteSor;
    $[3] = effectiveIsEditable;
    $[4] = isEditing;
    $[5] = pathname;
    $[6] = priorAuthorizations;
    $[7] = referral;
    $[8] = router;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = T3;
    $[13] = T4;
    $[14] = cardActionsProps;
    $[15] = diagnosisList;
    $[16] = hasDiagnoses;
    $[17] = referringFax;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    T3 = $[12];
    T4 = $[13];
    cardActionsProps = $[14];
    diagnosisList = $[15];
    hasDiagnoses = $[16];
    referringFax = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[100] !== T0 || $[101] !== t3) {
    t17 = <T0 text={t3} />;
    $[100] = T0;
    $[101] = t3;
    $[102] = t17;
  } else {
    t17 = $[102];
  }
  let t18;
  if ($[103] !== t17 || $[104] !== t4 || $[105] !== t5) {
    t18 = <div className={t4}>{t5}{t17}</div>;
    $[103] = t17;
    $[104] = t4;
    $[105] = t5;
    $[106] = t18;
  } else {
    t18 = $[106];
  }
  let t19;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="font-semibold block">Reason for Referral</span>;
    $[107] = t19;
  } else {
    t19 = $[107];
  }
  const t20 = referral.reasonForReferral || "N/A";
  let t21;
  if ($[108] !== t20) {
    t21 = <div className="space-y-1">{t19}<CopyableContent text={t20} /></div>;
    $[108] = t20;
    $[109] = t21;
  } else {
    t21 = $[109];
  }
  let t22;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="font-semibold block">Scheduling Priority</span>;
    $[110] = t22;
  } else {
    t22 = $[110];
  }
  const t23 = referral.schedulingPriority || "N/A";
  let t24;
  if ($[111] !== t23) {
    t24 = <div className="space-y-1">{t22}<CopyableContent text={t23} /></div>;
    $[111] = t23;
    $[112] = t24;
  } else {
    t24 = $[112];
  }
  let t25;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="font-semibold block">Referring Location Fax</span>;
    $[113] = t25;
  } else {
    t25 = $[113];
  }
  let t26;
  if ($[114] !== referringFax) {
    t26 = <div className="space-y-1">{t25}<CopyableContent text={referringFax} /></div>;
    $[114] = referringFax;
    $[115] = t26;
  } else {
    t26 = $[115];
  }
  let t27;
  if ($[116] !== referral.section?.reviewedContent?.isVaMilitaryReferral) {
    t27 = !!referral.section?.reviewedContent?.isVaMilitaryReferral && <div className="col-span-3 space-y-1"><Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge></div>;
    $[116] = referral.section?.reviewedContent?.isVaMilitaryReferral;
    $[117] = t27;
  } else {
    t27 = $[117];
  }
  let t28;
  if ($[118] !== t10 || $[119] !== t11 || $[120] !== t12 || $[121] !== t18 || $[122] !== t21 || $[123] !== t24 || $[124] !== t26 || $[125] !== t27 || $[126] !== t6 || $[127] !== t7 || $[128] !== t8 || $[129] !== t9) {
    t28 = <div className={t6}>{t7}{t8}{t9}{t10}{t11}{t12}{t18}{t21}{t24}{t26}{t27}</div>;
    $[118] = t10;
    $[119] = t11;
    $[120] = t12;
    $[121] = t18;
    $[122] = t21;
    $[123] = t24;
    $[124] = t26;
    $[125] = t27;
    $[126] = t6;
    $[127] = t7;
    $[128] = t8;
    $[129] = t9;
    $[130] = t28;
  } else {
    t28 = $[130];
  }
  let t29;
  if ($[131] !== diagnosisList || $[132] !== hasDiagnoses || $[133] !== referral.diagnosisCode || $[134] !== referral.diagnosisText) {
    t29 = hasDiagnoses && <div className="mt-6 space-y-2"><span className="font-semibold block text-sm">ICD-10 Diagnosis List</span><DiagnosisListTable diagnoses={diagnosisList} fallbackDiagnosis={referral.diagnosisCode || referral.diagnosisText ? {
        diagnosisCode: referral.diagnosisCode ?? null,
        diagnosisText: referral.diagnosisText ?? null
      } : null} /></div>;
    $[131] = diagnosisList;
    $[132] = hasDiagnoses;
    $[133] = referral.diagnosisCode;
    $[134] = referral.diagnosisText;
    $[135] = t29;
  } else {
    t29 = $[135];
  }
  let t30;
  if ($[136] !== T1 || $[137] !== t28 || $[138] !== t29) {
    t30 = <T1>{t28}{t29}</T1>;
    $[136] = T1;
    $[137] = t28;
    $[138] = t29;
    $[139] = t30;
  } else {
    t30 = $[139];
  }
  let t31;
  if ($[140] !== T2 || $[141] !== t13 || $[142] !== t14 || $[143] !== t30) {
    t31 = <T2 className={t13}>{t14}{t30}</T2>;
    $[140] = T2;
    $[141] = t13;
    $[142] = t14;
    $[143] = t30;
    $[144] = t31;
  } else {
    t31 = $[144];
  }
  let t32;
  if ($[145] !== T3 || $[146] !== t15 || $[147] !== t31) {
    t32 = <T3 asChild={t15}>{t31}</T3>;
    $[145] = T3;
    $[146] = t15;
    $[147] = t31;
    $[148] = t32;
  } else {
    t32 = $[148];
  }
  let t33;
  if ($[149] !== cardActionsProps || $[150] !== effectiveIsEditable) {
    t33 = effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[149] = cardActionsProps;
    $[150] = effectiveIsEditable;
    $[151] = t33;
  } else {
    t33 = $[151];
  }
  let t34;
  if ($[152] !== T4 || $[153] !== t32 || $[154] !== t33) {
    t34 = <T4>{t32}{t33}</T4>;
    $[152] = T4;
    $[153] = t32;
    $[154] = t33;
    $[155] = t34;
  } else {
    t34 = $[155];
  }
  return t34;
}
function _temp(pa) {
  return pa.endDate;
}